/**
 * Infinite scroll + JS masonry dla sekcji "Product list" (product-list.liquid) w Horizon.
 *
 * Sekcja musi zawierać:
 *  - wrapper z atrybutem data-section-id="{{ section.id }}" (dodajemy w kopii pliku sekcji)
 *  - kontener wyników z data-testid="resource-list-grid" (już jest w oryginale)
 *  - ukryty link <a data-infinite-scroll-next href="..."> z URL-em następnej strony
 *    (już jest w Twoim pliku sekcji)
 *
 * Masonry oparte na CSS Grid (grid-auto-rows: 1px + grid-row-end: span N),
 * zamiast natywnego grid-template-rows: masonry (niewspierane poza Firefoksem)
 * i zamiast fallbacku columns (który przestawia kolejność elementów przy doładowaniu).
 *
 * WYMAGANY towarzyszący CSS w custom.css:
 *
 * .resource-list.resource-list--grid {
 *   display: grid !important;
 *   grid-template-columns: var(--resource-list-columns, repeat(3, 1fr)) !important;
 *   grid-auto-rows: 1px !important;
 *   align-items: start !important;
 *   columns: unset !important;
 * }
 *
 * @media screen and (max-width: 749px) {
 *   .resource-list.resource-list--grid {
 *     grid-template-columns: var(--resource-list-columns-mobile, repeat(1, 1fr)) !important;
 *   }
 * }
 *
 * .resource-list--grid .infinite-scroll-item--entering {
 *   animation: infiniteScrollFadeIn 0.6s ease-out both;
 * }
 *
 * @keyframes infiniteScrollFadeIn {
 *   from { opacity: 0; transform: translateY(16px); }
 *   to { opacity: 1; transform: translateY(0); }
 * }
 *
 * @media (prefers-reduced-motion: reduce) {
 *   .resource-list--grid .infinite-scroll-item--entering {
 *     animation: none;
 *   }
 * }
 */

(function () {
  const ROW_HEIGHT = 1; // musi być zgodne z grid-auto-rows w CSS

  function layoutMasonry(grid) {
    const rowGap = parseFloat(getComputedStyle(grid).rowGap) || 0;
    Array.from(grid.children).forEach((item) => {
      const height = item.getBoundingClientRect().height;
      const span = Math.ceil((height + rowGap) / (ROW_HEIGHT + rowGap));
      item.style.gridRowEnd = `span ${span}`;
    });
  }

  function initSection(section) {
    const sectionId = section.dataset.sectionId;
    const grid = section.querySelector('[data-testid="resource-list-grid"]');
    let nextLink = section.querySelector('a[data-infinite-scroll-next]');

    if (!sectionId || !grid) return;

    // Relayout debounced przez requestAnimationFrame
    let rafId = null;
    const scheduleLayout = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        layoutMasonry(grid);
      });
    };

    const resizeObserver = new ResizeObserver(scheduleLayout);
    const observeAllChildren = () => {
      Array.from(grid.children).forEach((item) => resizeObserver.observe(item));
    };
    observeAllChildren();

    // Pierwsze przeliczenie dopiero gdy WSZYSTKO na stronie (w tym obrazki) się załaduje
    if (document.readyState === 'complete') {
      scheduleLayout();
    } else {
      window.addEventListener('load', scheduleLayout, { once: true });
    }

    // Dodatkowe zabezpieczenie: przelicz jeszcze raz 300ms po starcie,
    // na wypadek obrazków doładowanych asynchronicznie już po 'load'
    setTimeout(scheduleLayout, 300);

    window.addEventListener('resize', scheduleLayout);

    if (!nextLink) return;

    // Sentinel do obserwacji przewijania — wstawiamy zaraz za siatką produktów
    const sentinel = document.createElement('div');
    sentinel.setAttribute('data-infinite-scroll-sentinel', '');
    sentinel.style.height = '1px';
    grid.after(sentinel);

    // Prosty wskaźnik ładowania
    const spinner = document.createElement('div');
    spinner.textContent = 'Ładowanie kolejnych produktów…';
    spinner.style.cssText = 'width:100%;text-align:center;padding:1.5rem 0;opacity:0.7;font-size:0.9rem;';
    spinner.hidden = true;
    sentinel.after(spinner);

    let loading = false;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading && nextLink) {
          loadMore();
        }
      },
      { rootMargin: '600px 0px' }
    );
    observer.observe(sentinel);

    async function loadMore() {
      loading = true;
      spinner.hidden = false;

      try {
        const url = new URL(nextLink.href, window.location.origin);
        url.searchParams.set('section_id', sectionId);

        const response = await fetch(url.toString());
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const newGrid = doc.querySelector('[data-testid="resource-list-grid"]');
        if (newGrid) {
          const newItems = [];

          Array.from(newGrid.children).forEach((child, index) => {
            const clone = child.cloneNode(true);
            clone.classList.add('infinite-scroll-item--entering');
            clone.style.animationDelay = `${index * 60}ms`;
            clone.addEventListener(
              'animationend',
              () => {
                clone.classList.remove('infinite-scroll-item--entering');
                clone.style.animationDelay = '';
              },
              { once: true }
            );

            grid.appendChild(clone);
            resizeObserver.observe(clone);
            newItems.push(clone);
          });

          // Wstępne przybliżenie od razu (obrazy mogą jeszcze nie mieć wymiarów)
          scheduleLayout();

          // Docelowe przeliczenie, gdy obrazki nowych kart się doczytają
          const imgs = newItems.flatMap((item) => Array.from(item.querySelectorAll('img')));
          const imgPromises = imgs.map((img) =>
            img.complete
              ? Promise.resolve()
              : new Promise((res) => {
                  img.addEventListener('load', res, { once: true });
                  img.addEventListener('error', res, { once: true });
                })
          );
          Promise.all(imgPromises).then(scheduleLayout);
        }

        // Znajdź nowy link "next" (albo jego brak = koniec listy)
        const newNextLink = doc.querySelector('a[data-infinite-scroll-next]');
        if (newNextLink) {
          nextLink = { href: newNextLink.getAttribute('href') };
        } else {
          nextLink = null;
          observer.disconnect();
        }
      } catch (err) {
        console.error('[custom-infinite-scroll] Błąd doładowania produktów:', err);
      } finally {
        loading = false;
        spinner.hidden = true;
      }
    }
  }

  function init() {
    document
      .querySelectorAll('[data-testid="product-list"][data-section-id]')
      .forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
